import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/esm/extends";
var _excluded = ["value", "name", "onChange", "focused", "onFocusChange", "isFetching", "error", "blockedDates", "isContinuous", "showClearDate", "enablePastDays", "enableFutureDays", "disabled", "onMonthChange", "initialVisibleMonth", "anchorDirection", "startDatePlaceholderText", "endDatePlaceholderText", "startDateId", "endDateId", "style", "className", "jss"];
import React, { useState, useMemo, forwardRef } from 'react';
import PropTypes from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';

var BtnCalendar = function BtnCalendar(props) {
  return /*#__PURE__*/React.createElement("svg", props, /*#__PURE__*/React.createElement("path", {
    d: "M18.5 2h-4V0h-1v2h-6V0h-1v2h-4A2.503 2.503 0 0 0 0 4.5v12C0 17.879 1.122 19 2.5 19h16c1.379 0 2.5-1.121 2.5-2.5v-12C21 3.122 19.879 2 18.5 2zm-16 1h4v.667h1V3h6v.667h1V3h4c.827 0 1.5.673 1.5 1.5V7H1V4.5C1 3.673 1.673 3 2.5 3zm16 15h-16c-.827 0-1.5-.673-1.5-1.5V8h19v8.5c0 .827-.673 1.5-1.5 1.5z"
  }));
};

BtnCalendar.defaultProps = {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 21 19"
};
import { END_DATE, START_DATE, OPEN_DOWN } from 'react-dates/lib/constants';
import 'react-dates/initialize';
import { DateRangePicker as ReactDatesPicker, CalendarDay } from 'react-dates';
import moment from 'moment';
import cn from 'classnames';
import createStyles from "../../createStyles";
import { uuid } from "../../utils/commonUtility";
import Row from "../../Layout/Row";
import PickerClearButton from "../../basePicker/PickerClearButton";
import DatePickerStyles from "../styles";
import NavButton from "../NavButton";
import PickerInputButton from "../../basePicker/PickerInputButton";
import { DATE_FORMAT, DATE_DISPLAY_FORMAT, MONTH_DISPLAY_FORMAT } from "../constants";
import CustomArrowIcon from "../CustomArrowIcon";
export var useStyles = createStyles(function (theme) {
  return {
    root: DatePickerStyles.root(theme),
    inputContainer: _extends({}, DatePickerStyles.inputContainer, {
      paddingRight: theme.spacer(3)
    }),
    focused: DatePickerStyles.focused(theme),
    error: DatePickerStyles.error(theme),
    disabled: DatePickerStyles.disabled(theme)
  };
}, {
  name: 'DateRangePicker'
});
var DateRangePicker = /*#__PURE__*/forwardRef(function DateRangePicker(_ref, ref) {
  var value = _ref.value,
      name = _ref.name,
      onChange = _ref.onChange,
      focusedProp = _ref.focused,
      onFocusChange = _ref.onFocusChange,
      isFetching = _ref.isFetching,
      error = _ref.error,
      blockedDates = _ref.blockedDates,
      isContinuous = _ref.isContinuous,
      _ref$showClearDate = _ref.showClearDate,
      showClearDate = _ref$showClearDate === void 0 ? true : _ref$showClearDate,
      enablePastDays = _ref.enablePastDays,
      _ref$enableFutureDays = _ref.enableFutureDays,
      enableFutureDays = _ref$enableFutureDays === void 0 ? true : _ref$enableFutureDays,
      disabled = _ref.disabled,
      onMonthChange = _ref.onMonthChange,
      initialVisibleMonth = _ref.initialVisibleMonth,
      _ref$anchorDirection = _ref.anchorDirection,
      anchorDirection = _ref$anchorDirection === void 0 ? 'left' : _ref$anchorDirection,
      startDatePlaceholderText = _ref.startDatePlaceholderText,
      endDatePlaceholderText = _ref.endDatePlaceholderText,
      startDateId = _ref.startDateId,
      endDateId = _ref.endDateId,
      style = _ref.style,
      className = _ref.className,
      jss = _ref.jss,
      rest = _objectWithoutProperties(_ref, _excluded);

  var classes = useStyles({
    jss: jss
  });

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focusedState = _useState2[0],
      setFocusState = _useState2[1];

  var inputFocus = focusedProp || focusedState;

  var renderMonthElement = function renderMonthElement(_ref2) {
    var month = _ref2.month;
    return month.format(MONTH_DISPLAY_FORMAT);
  };

  var startDateInputId = useMemo(function () {
    return startDateId || uuid();
  }, [startDateId]);
  var endDateInputId = useMemo(function () {
    return endDateId || uuid();
  }, [endDateId]);

  var handleFocusChange = function handleFocusChange(focusedInput) {
    if (onFocusChange) {
      onFocusChange(focusedInput);
    }

    setFocusState(focusedInput);
  };

  var isOutsideRange = function isOutsideRange(date) {
    var isPastOutsideRange = !enablePastDays && date.isBefore(moment(), 'day');
    var isFutureOutsideRange = !enableFutureDays && date.isAfter(moment(), 'day');
    return isPastOutsideRange || isFutureOutsideRange;
  };

  var handleDatesChange = function handleDatesChange(_ref3) {
    var startDate = _ref3.startDate,
        endDate = _ref3.endDate;

    if (onChange) {
      // If startDate changes - reset endDate
      if (value !== null && value !== void 0 && value.startDate && value.startDate !== startDate) {
        return onChange({
          startDate: startDate,
          endDate: undefined
        }, name);
      }

      onChange({
        startDate: startDate,
        endDate: endDate
      }, name);
    }
  };

  var handleClearDates = function handleClearDates() {
    handleDatesChange({
      startDate: undefined,
      endDate: undefined
    });
  };

  var upperBound = useMemo(function () {
    if (isContinuous) {
      if (value !== null && value !== void 0 && value.startDate && blockedDates) {
        var formattedStartDate = value.startDate.format(DATE_FORMAT);
        return Array.from(blockedDates).sort().find(function (blockedDay) {
          return blockedDay > formattedStartDate;
        });
      }
    }
  }, [value === null || value === void 0 ? void 0 : value.startDate, blockedDates, isContinuous]);

  var renderDayContents = function renderDayContents(day) {
    return isFetching ? '...' : day.date();
  };

  var blockedDatesSet = useMemo(function () {
    return new Set(blockedDates || []);
  }, [blockedDates]);

  var isDayBlocked = function isDayBlocked(day) {
    var formattedDay = day.format(DATE_FORMAT);

    if (isContinuous) {
      if (inputFocus === END_DATE && upperBound) {
        return formattedDay > upperBound || formattedDay < upperBound && !!(blockedDatesSet !== null && blockedDatesSet !== void 0 && blockedDatesSet.has(formattedDay));
      }
    }

    return isFetching || !!(blockedDatesSet !== null && blockedDatesSet !== void 0 && blockedDatesSet.has(formattedDay));
  };

  var handleCalendarButtonClick = function handleCalendarButtonClick() {
    if (!inputFocus) {
      if (onFocusChange) {
        onFocusChange(START_DATE);
      }

      setFocusState(START_DATE);
    }
  };

  var getInitialVisibleMonth = function getInitialVisibleMonth() {
    return initialVisibleMonth ? moment(initialVisibleMonth) : moment();
  };

  return /*#__PURE__*/React.createElement(Row, {
    ref: ref,
    justify: "between",
    className: cn(classes.root, 'date-picker', className, _defineProperty({}, classes.focused, inputFocus), _defineProperty({}, classes.error, error), _defineProperty({}, classes.disabled, disabled)),
    style: style
  }, /*#__PURE__*/React.createElement(PickerInputButton, {
    disabled: disabled,
    onClick: handleCalendarButtonClick,
    svg: BtnCalendar,
    active: !!inputFocus,
    leftAligned: true
  }), /*#__PURE__*/React.createElement(Row, {
    className: classes.inputContainer
  }, /*#__PURE__*/React.createElement(ReactDatesPicker, _extends({
    renderMonthElement: renderMonthElement,
    readOnly: true,
    appendToBody: false,
    onDatesChange: handleDatesChange,
    focusedInput: inputFocus || undefined,
    onFocusChange: handleFocusChange,
    endDateId: endDateInputId,
    startDateId: startDateInputId,
    disabled: disabled,
    startDatePlaceholderText: startDatePlaceholderText,
    endDatePlaceholderText: endDatePlaceholderText,
    startDate: value === null || value === void 0 ? void 0 : value.startDate,
    endDate: value === null || value === void 0 ? void 0 : value.endDate,
    isDayBlocked: isDayBlocked,
    daySize: 40,
    noBorder: true,
    openDirection: OPEN_DOWN,
    onNextMonthClick: onMonthChange,
    onPrevMonthClick: onMonthChange,
    numberOfMonths: 1,
    hideKeyboardShortcutsPanel: true,
    verticalSpacing: 10,
    renderDayContents: renderDayContents,
    renderCalendarDay: function renderCalendarDay(props) {
      return /*#__PURE__*/React.createElement(CalendarDay, _extends({}, props, {
        isFocused: false
      }));
    },
    navPrev: /*#__PURE__*/React.createElement(NavButton, {
      direction: "Left"
    }),
    navNext: /*#__PURE__*/React.createElement(NavButton, {
      direction: "Right"
    }),
    customArrowIcon: /*#__PURE__*/React.createElement(CustomArrowIcon, {
      isInputEmpty: !(value !== null && value !== void 0 && value.startDate) && !(value !== null && value !== void 0 && value.endDate)
    }),
    isOutsideRange: isOutsideRange,
    initialVisibleMonth: getInitialVisibleMonth,
    anchorDirection: anchorDirection,
    displayFormat: DATE_DISPLAY_FORMAT
  }, rest)), /*#__PURE__*/React.createElement(PickerClearButton, {
    show: showClearDate && ((value === null || value === void 0 ? void 0 : value.startDate) || (value === null || value === void 0 ? void 0 : value.endDate)),
    onClick: handleClearDates
  })));
});
DateRangePicker.displayName = 'DateRangePicker';
export default DateRangePicker;
DateRangePicker.propTypes = {
  /** the picked date range */
  value: PropTypes.shape({
    startDate: momentPropTypes.momentObj,
    endDate: momentPropTypes.momentObj
  }),

  /** date picker name to be set */
  name: PropTypes.string,

  /** Callback to be called on picker value change */
  onChange: PropTypes.func,

  /** Control focus state - focued => picker opens. Not required - focus state is uncontrolled by default */
  focused: PropTypes.bool,

  /** Callback to be called when focus state changes. Called with a boolean  */
  onFocusChange: PropTypes.func,

  /** Fetching state */
  isFetching: PropTypes.bool,

  /** Indicator for form error state  */
  error: PropTypes.bool,

  /** Array of blocked dates - formatted as YYYY-MM-DD */
  blockedDates: PropTypes.arrayOf(PropTypes.string),

  /** accept only a continuous range. Meaning, it cannot contain blocked days */
  isContinuous: PropTypes.bool,

  /** When dates is selected - show a clear button */
  showClearDate: PropTypes.bool,

  /** Enable past days */
  enablePastDays: PropTypes.bool,

  /** Enable future days */
  enableFutureDays: PropTypes.bool,

  /** Disable state in which picker interaction is not available */
  disabled: PropTypes.bool,

  /** callback to be called when a switching months. Called with a momnet object of the new month */
  onMonthChange: PropTypes.func,

  /** month to be show when the dialog picker opens - formatted as YYYY-MM-DD */
  initialVisibleMonth: PropTypes.string,

  /** Anchor direction to open the picker - */
  anchorDirection: PropTypes.oneOf(['left', 'right']),

  /** Placeholder for the start date */
  startDatePlaceholderText: PropTypes.string,

  /** Placeholder for the end date */
  endDatePlaceholderText: PropTypes.string,

  /** id for the start date */
  startDateId: PropTypes.string,

  /** id for the end date */
  endDateId: PropTypes.string,

  /** class name to be added to the root */
  className: PropTypes.string,

  /** style to be added to the root */
  style: PropTypes.shape(),
  jss: PropTypes.oneOfType([PropTypes.shape(), PropTypes.func])
};